import type { ClinicalNeed, NeedId } from '../types/filtek'
import { imagery } from './imagery'
import { brand } from './palette'

export const needs: ClinicalNeed[] = [
  {
    id: 'aesthetic',
    index: '01',
    label: 'Maximum aesthetic control',
    demand: 'The case is visible, the patient is watching, and the margin has nowhere to hide.',
    resolution: 'Full command of opacity, translucency and polish — layer by layer.',
    technique: 'Layered stratification',
    techniqueSteps: [
      'Select dentin opacity one shade deeper than the body shade.',
      'Build the palatal enamel shell first, then the dentin core.',
      'Leave space for a facial enamel layer of 0.5 mm.',
      'Finish with contour, then gloss — texture before polish.'
    ],
    productId: 'supreme-ultra',
    accent: brand.mint,
    image: imagery.anterior,
    evidence: '10-year clinical follow-up on nanofiller composite restorations shows retained gloss and marginal integrity.'
  },
  {
    id: 'shade',
    index: '02',
    label: 'Shade confidence',
    demand: 'The shade guide says one thing, the tooth says another, and the patient is waiting.',
    resolution: 'Three shade options instead of thirty — chosen in seconds, not deliberated.',
    technique: 'Single-shade selection',
    techniqueSteps: [
      'Assess the tooth wet, before isolation, under neutral light.',
      'Choose Light, Medium or Dark — no secondary shade mapping.',
      'Place in increments, adapting to the cavity walls.',
      'Polish and reassess hydrated — the shade settles into its surroundings.'
    ],
    productId: 'easy-match',
    accent: brand.green,
    image: imagery.enamel,
    evidence: 'Shade-matching studies report clinically acceptable matches across the everyday VITA range from three options.'
  },
  {
    id: 'everyday',
    index: '03',
    label: 'Everyday simplicity',
    demand: 'Most of the week is not a complex case. It is the same reliable restoration, repeated.',
    resolution: 'One sculptable material that behaves the same way anterior and posterior.',
    technique: 'Single-material workflow',
    techniqueSteps: [
      'One shade from a set of eight, front or back.',
      'Place in increments up to 2 mm and sculpt directly.',
      'Non-slump body holds anatomy without rebound.',
      'Two-step polish to final gloss.'
    ],
    productId: 'universal',
    accent: brand.lime,
    image: imagery.layers,
    evidence: 'Eight shades cover 19 VITA shades, reducing inventory without reducing match quality.'
  },
  {
    id: 'posterior',
    index: '04',
    label: 'Posterior efficiency',
    demand: 'Deep, wide, hard to reach — and the next patient is already in the waiting room.',
    resolution: 'One increment to 5 mm. Fill, sculpt, cure, move on.',
    technique: 'Bulk fill placement',
    techniqueSteps: [
      'Isolate and establish the proximal matrix and wedge.',
      'Place a single increment up to 5 mm — no capping layer needed.',
      'Sculpt cusps and fissures before curing.',
      'Cure for 20 seconds per surface, then occlusal adjust.'
    ],
    productId: 'one-bulk',
    accent: brand.emerald,
    image: imagery.posterior,
    evidence: 'Low shrinkage stress chemistry supports 5 mm increments with marginal performance comparable to incremental placement.'
  },
  {
    id: 'flow',
    index: '05',
    label: 'Flow and adaptation',
    demand: 'The cavity has corners an instrument will never reach cleanly.',
    resolution: 'A material that self-levels into the detail, then stays exactly where it settled.',
    technique: 'Flowable base and liner',
    techniqueSteps: [
      'Dry the prep and confirm bonding protocol is complete.',
      'Express slowly into the deepest point and let it level.',
      'Limit the base to 4 mm, keeping the cavo-surface clear.',
      'Cap with a sculptable Filtek restorative and finish.'
    ],
    productId: 'bulk-flow',
    companionId: 'supreme-flow',
    accent: brand.aqua,
    image: imagery.flow,
    evidence: 'Self-levelling flowable bases show reduced void formation in deep proximal boxes compared with packable-only placement.'
  }
]

export const needById = (id?: NeedId): ClinicalNeed =>
  needs.find((need) => need.id === id) ?? needs[0]
